/**
 * Locked Users Management — SuperAdmin Page
 *
 * Shows all followup users with active lockout or permanent lock.
 * SuperAdmin can unlock any user with one click.
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const LockedUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unlocking, setUnlocking] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const fetchLockedUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/api/followup/locked-users');
      if (res.data.success) {
        setUsers(res.data.users || []);
      } else {
        setError(res.data.message || 'Failed to fetch');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLockedUsers();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchLockedUsers, 30000);
    return () => clearInterval(interval);
  }, [fetchLockedUsers]);

  const handleUnlock = async (email) => {
    if (!window.confirm(`Unlock ${email}? They will be able to request new OTP immediately.`)) return;

    setUnlocking(email);
    setSuccessMsg(null);
    try {
      const res = await api.post('/api/followup/unlock-user', { email });
      if (res.data.success) {
        setSuccessMsg(`${email} has been unlocked.`);
        fetchLockedUsers();
      } else {
        setError(res.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setUnlocking(null);
    }
  };

  const getStageLabel = (stage) => {
    switch (stage) {
      case 1: return 'Stage 1 (30 min served)';
      case 2: return 'Stage 2 (1 hr served)';
      case 3: return 'Permanent';
      default: return `Stage ${stage}`;
    }
  };

  const getStatusBadge = (user) => {
    if (user.isPermanent) {
      return <span style={{ background: '#dc2626', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>PERMANENT</span>;
    }
    if (user.isActive) {
      return <span style={{ background: '#d97706', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>LOCKED ({user.remainingMinutes} min left)</span>;
    }
    return <span style={{ background: '#059669', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>UNLOCKED</span>;
  };

  return (
    <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#1a1a2e' }}>
          🔒 Locked Users
        </h2>
        <button
          onClick={fetchLockedUsers}
          disabled={loading}
          style={{
            padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db',
            borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontFamily: 'inherit',
          }}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', color: '#dc2626', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {successMsg && (
        <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: '6px', padding: '10px 14px', marginBottom: '16px', color: '#166534', fontSize: '13px' }}>
          {successMsg}
        </div>
      )}

      {!loading && users.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', background: '#f9fafb', borderRadius: '8px', color: '#6b7280' }}>
          <p style={{ fontSize: '16px', margin: '0 0 8px' }}>No locked users</p>
          <p style={{ fontSize: '13px', margin: 0 }}>All users can access the follow-up form normally.</p>
        </div>
      )}

      {users.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: 600 }}>Email</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: 600 }}>Status</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: 600 }}>Stage</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: 600 }}>Last Attempt</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: 600 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '10px 12px' }}>
                  <strong>{user.email}</strong>
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                  {getStatusBadge(user)}
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'center', color: '#6b7280' }}>
                  {getStageLabel(user.lockoutStage)}
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'center', color: '#6b7280' }}>
                  {user.lastAttempt ? new Date(user.lastAttempt).toLocaleString() : 'N/A'}
                </td>
                <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                  <button
                    onClick={() => handleUnlock(user.email)}
                    disabled={unlocking === user.email}
                    style={{
                      padding: '6px 14px',
                      background: unlocking === user.email ? '#9ca3af' : '#059669',
                      color: '#fff', border: 'none', borderRadius: '4px',
                      cursor: unlocking === user.email ? 'not-allowed' : 'pointer',
                      fontSize: '12px', fontWeight: 600, fontFamily: 'inherit',
                    }}
                  >
                    {unlocking === user.email ? 'Unlocking...' : 'Unlock'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: '20px', fontSize: '12px', color: '#9ca3af', textAlign: 'center' }}>
        Auto-refreshes every 30 seconds • Lockout stages: Stage 1 (30 min) → Stage 2 (1 hr) → Permanent
      </div>
    </div>
  );
};

export default LockedUsers;
